import Accordion from "@/common/components/Accordion"
import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import Checkbox from "@/common/components/Checkbox"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader"
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader"
import SelectController from "@/common/components/SelectController"
import useBlobs from "@/common/hooks/queries/useBlobs"
import useCompany from "@/common/hooks/queries/useCompany"
import useCompanyEquipment from "@/common/hooks/queries/useCompanyEquipment"
import useDebounce from "@/common/hooks/useDebounce";
import { useGoBack } from "@/common/hooks/useGoBack";
import { Mapper } from "@/common/services/mapper"
import { FormTransformers } from "@/common/utils/formTransformers"
import { ApplicableProducts } from "@/features/privateCompanies/components/createEquipmentForm/applicableProducts"
import { EquipmentDetails } from "@/features/privateCompanies/components/createEquipmentForm/equipmentDetails"
import { PeriodRestriction } from "@/features/privateCompanies/components/createEquipmentForm/periodRestriction"
import useUpdateCompanyEquipment from "@/features/privateCompanies/hooks/useUpdateCompanyEquipment"
import { companyEquipmentSchema, CompanyEquipmentValidationSchema } from "@/features/privateCompanies/schemas/createEquipment.schema"
import { CompanyBranchData } from "@/features/privateCompanies/types/companyBranches.type"
import { companyEquipmentDefaultValues } from "@/features/privateCompanies/utils/companyEquipmentDefaultValues"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { type Option } from '@/common/types/option'
import { ShowHeader } from "@/common/components/ShowHeader";
import { CarFrontIcon } from "lucide-react"
import { EquipmentsTransformer } from "@/common/services/equipmentTransformer";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches"

const Update = () => {
  const { companyId, id: equipmentId } = useParams()
  const queryClient = useQueryClient()
  const goBack = useGoBack();

  const [searchBranch, setSearchBranch] = useDebounce('');
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { data: company } = useCompany({ id: companyId || '' })

  const {
    data: equipment,
    isLoading: equipmentIsPending,
    dataUpdatedAt: equipmentUpdatedAt,
  } = useCompanyEquipment({ id: equipmentId || '' })

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const equipmentDisplayName = useMemo(() => {
    return (categoryFieldForDisplayName && equipment[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
      equipment?.license_plate ||
      equipment?.official_name ||
      equipment?.serial_number ||
      'Não informado';
  }, [equipmentUpdatedAt]);

  const { data: companyBranchOptions } = useAvailableCompanyBranches({
    companyId,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    setError,
    formState: { errors }
  } = useForm<CompanyEquipmentValidationSchema>({
    resolver: zodResolver(companyEquipmentSchema),
    defaultValues: companyEquipmentDefaultValues({
      equipmentData: equipment ?? undefined,
      companyBranchOptions: [],
    })
  })

  const {
    mutate: updateCompanyEquipment,
    isPending: updateCompanyEquipmentIsPending,
    isError: updateCompanyEquipmentIsError,
  } = useUpdateCompanyEquipment({
    equipmentId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyEquipments'] })
      goBack({ fallback: `/companies/${companyId}/equipments/${equipmentId}` })
    },
    onError: FormTransformers.errorsTransformer<CompanyEquipmentValidationSchema>({
      setError,
      setMessages,
      transformer: EquipmentsTransformer.errorsTransformer(),
    }),
  })

  const onSubmit = (data: CompanyEquipmentValidationSchema) => {
    updateCompanyEquipment({
      form: data,
      equipmentData: equipment,
    })
  }

  useEffect(() => {
    if (!equipmentIsPending && equipment?.branch) {
      const branchOption = [{
        label: equipment.branch.display_name,
        value: equipment.branch.id
      }] as Option[]

      reset(companyEquipmentDefaultValues({
        equipmentData: equipment,
        companyBranchOptions: branchOption,
      }))
    }
  }, [equipmentIsPending]);

  const documents = watch('documents') || []
  const equipmentTier = watch('equipment_tier')

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/companies/${companyId}/equipments/${equipmentId}`,
        }}
        isLoading={equipmentIsPending}
        titleClassName="text-white-400 font-normal text-base"
        title={company?.general_information?.fantasy_name}
        subtitle={(
          <div className='flex items-center gap-1.5'>
            {categoryFieldForDisplayName === 'license_plate' && (
              <CarFrontIcon strokeWidth={1.5} size={18} />
            )}

            <h2 data-testid="equipment_show_display_name">
              {equipmentDisplayName}
            </h2>
          </div>
        )}
        buttons={(
          <div className="flex gap-x-4 items-center">
            <Button
              variant="outline-error"
              onClick={() => goBack({ fallback: `/companies/${companyId}/equipments/${equipmentId}` })}
            >
              Cancelar
            </Button>

            <Button
              name="submit"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={updateCompanyEquipmentIsPending}
              disabled={uploadBlobIsPending}
            >
              Salvar
            </Button>
          </div>
        )}
      />

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-128px)]">
        <p className="text-lg font-bold">Dados do equipamento</p>
        <MultipleImageUploader
          label="Anexe uma foto"
          name="equipment-images"
          accept="image/jpeg, image/png"
          uploadBlob={uploadBlob}
          control={control}
          fieldName="photos"
          disabled={uploadBlobIsPending || updateCompanyEquipmentIsPending}
          errorMessage={errors.photos?.message || undefined}
        />
        <SelectController
          control={control}
          name="branch_id"
          label="Filial"
          markAsRequired
          options={companyBranchOptions || []}
          onInputChange={setSearchBranch}
          errorMessage={errors.branch_id?.message || undefined}
          isDisabled={updateCompanyEquipmentIsPending}
        />
        <PeriodRestriction
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={updateCompanyEquipmentIsPending}
        />
        <EquipmentDetails
          equipment={equipment}
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={updateCompanyEquipmentIsPending}
          updateDisabled
        />
        <Accordion
          head={
            <Checkbox
              label="Documentos do equipamento"
              name="equipment_documents"
              boxClassName="w-[24px] h-[24px]"
              checked={documents.length > 0}
              disabled
            />
          }
          body={(
            <div className="flex w-full flex-col gap-4 text-white-800">
              <MultipleDocumentUploader
                title="Anexe um documento do equipamento"
                name="equipment-documents"
                fieldName="documents"
                accept="application/pdf"
                uploadBlob={uploadBlob}
                control={control}
                disabled={uploadBlobIsPending || updateCompanyEquipmentIsPending}
              />
            </div>
          )}
        />
        <ApplicableProducts
          cardId={equipment?.card?.id}
          equipmentTierId={equipmentTier?.value}
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={updateCompanyEquipmentIsPending}
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={updateCompanyEquipmentIsError}
        />
      )}
    </form>
  )
}

export default Update
